<template>
    <div class="edit-list">
        <!-- 标题栏 -->
        <div class="edit-header">
            <div class="title">编辑列表</div>
            <div class="info">
                <span class="count">{{ images.length }} 张</span>
                <span v-if="currentIndex >= 0" class="current">
                    · 第 {{ currentIndex + 1 }} 张
                </span>
            </div>
        </div>

        <!-- 图片列表 -->
        <div class="edit-list-scroll">
            <!-- 第一个插入按钮 - 显示在顶部 -->
            <!-- <div v-if="images.length > 0"
                class="insert-blank-btn top-insert"
                @click="handleInsertBlank(0)"
                title="在顶部插入空白图片"
            >
                <PlusOutlined />
            </div> -->

            <draggable v-model="editImages" @change="handleReorder" item-key="requestId" :animation="200"
                handle=".drag-handle" ghost-class="ghost-item">
                <template #item="{ element: image, index }">
                    <div class="edit-item-container">
                        <!-- 插入空白图片按钮 - 显示在图片之间 -->
                        <div v-if="shouldShowInsertButton(index)" class="insert-blank-btn"
                            @click="handleInsertBlank(index)" title="在此处插入空白图片">
                            <PlusOutlined />
                        </div>

                        <div class="edit-item-wrapper" @click="handleSwitch(image.requestId)">
                            <!-- 拖拽手柄 -->
                            <div class="drag-handle" title="拖拽调整顺序">
                                <MenuOutlined />
                            </div>

                            <ImageItem :image="image" :active="image.requestId === currentId" :type="'edit'"
                                :size="'small'" />

                            <!-- 已编辑标签 -->
                            <div v-if="editResultMap[image.requestId]?.url" class="status-badge edited">
                                <CheckCircleOutlined /> 已编辑
                            </div>

                            <!-- 移除按钮 -->
                            <div class="remove-btn" @click.stop="handleRemove(image.requestId)">
                                <CloseOutlined />
                            </div>
                        </div>
                    </div>
                </template>
            </draggable>

            <!-- 空状态 -->
            <div v-if="images.length === 0" class="empty-state">
                <InboxOutlined class="empty-icon" />
                <p>从左侧选择图片开始编辑</p>
                <p class="hint">点击待选列表中的图片添加</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import draggable from 'vuedraggable';
import { CheckCircleOutlined, InboxOutlined, CloseOutlined, MenuOutlined, PlusOutlined } from '@ant-design/icons-vue';
import ImageItem from './ImageItem.vue';
import { createBlankImageObject } from '@/utils/blankImageGenerator';

const props = defineProps({
    images: {
        type: Array,
        required: true,
    },
    currentId: {
        type: String,
        default: '',
    },
    editResultMap: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['switch-image', 'remove-image', 'reorder', 'insert-blank']);

// 编辑列表（用于 v-model）
const editImages = computed({
    get: () => props.images,
    set: (value) => emit('reorder', value),
});

// 当前索引
const currentIndex = computed(() => {
    return props.images.findIndex(img => img.requestId === props.currentId);
});

// 拖拽排序处理
const handleReorder = () => {
    // draggable 会自动更新 v-model，触发 emit('reorder')
};

// 切换图片
const handleSwitch = (requestId) => {
    if (requestId !== props.currentId) {
        emit('switch-image', requestId);
    }
};

// 移除图片
const handleRemove = (requestId) => {
    emit('remove-image', requestId);
};

// 智能显示条件 - 只有非第一个图片才显示插入按钮
const shouldShowInsertButton = (index) => {
    return index > 0; // 只有索引>0（非第一个图片）才显示插入按钮
};

// 插入空白图片 - 在当前图片之前插入（与视觉位置一致）
const handleInsertBlank = (index) => {
    const blankImage = createBlankImageObject();
    emit('insert-blank', blankImage, index);
};
</script>

<style lang="less" scoped>
.edit-list {
    width: 100px;
    display: flex;
    flex-direction: column;
    // background: #fafafa;
    background: #ffffff;
    flex-shrink: 0;
}

.edit-header {
    padding: 12px;
    border-bottom: 1px solid #e0e0e0;
    background: #fff;

    .title {
        font-weight: 600;
        font-size: 13px;
        margin-bottom: 4px;
    }

    .info {
        font-size: 11px;
        color: #999;

        .current {
            color: @primary-color;
            font-weight: 500;
        }
    }
}

.edit-list-scroll {
    flex: 1;
    overflow-y: auto;
    padding: 12px;
}

.edit-item-wrapper {
    position: relative;
    margin-bottom: 10px;
    cursor: pointer;

    &:hover {

        .remove-btn,
        .drag-handle {
            opacity: 1;
        }
    }
}

.drag-handle {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 24px;
    height: 24px;
    background: rgba(0, 0, 0, 0.6);
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    opacity: 0;
    transition: opacity 0.2s;
    cursor: move;
    z-index: 15;
    font-size: 12px;

    &:hover {
        background: rgba(0, 0, 0, 0.8);
    }
}

.remove-btn {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 20px;
    height: 20px;
    background: rgba(255, 77, 79, 0.9);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    opacity: 0;
    transition: opacity 0.2s;
    cursor: pointer;
    z-index: 10;
    font-size: 12px;

    &:hover {
        background: #ff4d4f;
    }
}

.status-badge {
    position: absolute;
    right: 8px;
    bottom: 8px;
    padding: 3px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: bold;
    color: white;
    display: flex;
    align-items: center;
    gap: 4px;
    z-index: 5;
    pointer-events: none;

    &.edited {
        background: #52c41a;
    }
}

.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #999;

    .empty-icon {
        font-size: 48px;
        color: #d9d9d9;
        margin-bottom: 16px;
    }

    p {
        font-size: 12px;
        margin: 8px 0 0;

        &.hint {
            font-size: 11px;
            color: #bbb;
        }
    }
}

// 拖拽时的幽灵样式
:deep(.ghost-item) {
    opacity: 0.5;
    background: #f0f2f5;
    border: 2px dashed #2B8CFF;
}

// 插入空白图片按钮样式
.edit-list-scroll {
    position: relative;
}

.insert-blank-btn {
    width: 24px;
    height: 24px;
    background: #1890ff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
    transition: all 0.2s ease;
    opacity: 0;
    margin: 8px auto;

    &:hover {
        background: #40a9ff;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
    }
}

.top-insert {
    opacity: 0.8; // 顶部按钮保持可见
    margin-bottom: 4px;

    &:hover {
        opacity: 1;
    }
}

.edit-item-container {
    position: relative;

    .insert-blank-btn {
        position: absolute;
        top: -16px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 20;
        opacity: 0;
    }

    &:hover .insert-blank-btn {
        opacity: 1;
    }
}
</style>
